/**
 * Model Manifest - Configuration-driven model management
 * Step 3: Defines manifest schema, loading, and validation
 * V1.1: Added routing fields (tier, tags, minRecommendedMemoryGB)
 * V1.2: Added signature verification support
 */
import { verifyManifest } from '../registry/verify.js';
// === Valid Engine Types (for validation) ===
const VALID_ENGINES = ['webllm'];
// === Validation Functions ===
/**
 * Validate a single manifest entry
 */
function validateEntry(entry, index) {
    const errors = [];
    const prefix = `models[${index}]`;
    if (typeof entry !== 'object' || entry === null) {
        return { valid: false, errors: [`${prefix}: must be an object`] };
    }
    const e = entry;
    // Required: id
    if (typeof e.id !== 'string' || e.id.trim() === '') {
        errors.push(`${prefix}.id: must be a non-empty string`);
    }
    // Required: displayName
    if (typeof e.displayName !== 'string' || e.displayName.trim() === '') {
        errors.push(`${prefix}.displayName: must be a non-empty string`);
    }
    // Required: engine
    if (typeof e.engine !== 'string') {
        errors.push(`${prefix}.engine: must be a string`);
    }
    else if (!VALID_ENGINES.includes(e.engine)) {
        errors.push(`${prefix}.engine: invalid value "${e.engine}". Valid engines: ${VALID_ENGINES.join(', ')}`);
    }
    // Optional: sizeMB (must be positive number if present)
    if (e.sizeMB !== undefined && (typeof e.sizeMB !== 'number' || e.sizeMB <= 0)) {
        errors.push(`${prefix}.sizeMB: must be a positive number`);
    }
    // Optional: contextLength (must be positive integer if present)
    if (e.contextLength !== undefined &&
        (typeof e.contextLength !== 'number' || e.contextLength <= 0 || !Number.isInteger(e.contextLength))) {
        errors.push(`${prefix}.contextLength: must be a positive integer`);
    }
    // Optional: quantization (must be string if present)
    if (e.quantization !== undefined && typeof e.quantization !== 'string') {
        errors.push(`${prefix}.quantization: must be a string`);
    }
    // Optional: description (must be string if present)
    if (e.description !== undefined && typeof e.description !== 'string') {
        errors.push(`${prefix}.description: must be a string`);
    }
    return { valid: errors.length === 0, errors };
}
/**
 * Validate the entire manifest
 */
function validateManifest(data) {
    const errors = [];
    if (typeof data !== 'object' || data === null) {
        return { valid: false, errors: ['Manifest must be a JSON object'] };
    }
    const manifest = data;
    // Required: version
    if (manifest.version === undefined) {
        errors.push('version: required field is missing');
    }
    else if (typeof manifest.version !== 'string' && typeof manifest.version !== 'number') {
        errors.push('version: must be a string or number');
    }
    // Required: models array
    if (!Array.isArray(manifest.models)) {
        errors.push('models: must be an array');
        return { valid: false, errors };
    }
    if (manifest.models.length === 0) {
        errors.push('models: must contain at least one model');
    }
    // Validate each entry
    const seenIds = new Set();
    for (let i = 0; i < manifest.models.length; i++) {
        const entryResult = validateEntry(manifest.models[i], i);
        errors.push(...entryResult.errors);
        // Check for duplicate IDs
        const entry = manifest.models[i];
        if (typeof entry.id === 'string') {
            if (seenIds.has(entry.id)) {
                errors.push(`models[${i}].id: duplicate id "${entry.id}"`);
            }
            seenIds.add(entry.id);
        }
    }
    return { valid: errors.length === 0, errors };
}
/**
 * Parse and validate raw manifest data
 */
function parseManifest(data) {
    const result = validateManifest(data);
    if (!result.valid) {
        const errorList = result.errors.map((e) => `  - ${e}`).join('\n');
        throw new Error(`Invalid manifest:\n${errorList}`);
    }
    // Type assertion is safe after validation
    const raw = data;
    return {
        version: raw.version,
        models: raw.models.map((m) => {
            const entry = m;
            return {
                id: entry.id,
                displayName: entry.displayName,
                engine: entry.engine,
                sizeMB: entry.sizeMB,
                contextLength: entry.contextLength,
                quantization: entry.quantization,
                description: entry.description,
                tier: entry.tier,
                tags: entry.tags,
                minRecommendedMemoryGB: entry.minRecommendedMemoryGB,
            };
        }),
    };
}
// === Public API ===
/**
 * Load and validate a model manifest from a URL
 * @param manifestUrl - URL to fetch the manifest from
 * @returns Validated ModelManifest
 * @throws Error if fetch fails or manifest is invalid
 */
export async function loadManifest(manifestUrl) {
    let response;
    try {
        response = await fetch(manifestUrl);
    }
    catch (err) {
        throw new Error(`Failed to fetch manifest from "${manifestUrl}": ${err instanceof Error ? err.message : String(err)}`);
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch manifest from "${manifestUrl}": HTTP ${response.status} ${response.statusText}`);
    }
    let data;
    try {
        data = await response.json();
    }
    catch {
        throw new Error(`Failed to parse manifest from "${manifestUrl}": Invalid JSON`);
    }
    return parseManifest(data);
}
/**
 * Find a model by ID in the manifest
 * @param manifest - The loaded manifest
 * @param modelId - Model ID to find
 * @returns The model entry or undefined
 */
export function findModel(manifest, modelId) {
    return manifest.models.find((m) => m.id === modelId);
}
/**
 * Load manifest with optional signature verification (V1.2)
 * @param manifestUrl - URL to fetch the manifest from
 * @param registryConfig - Optional registry configuration for signature verification
 * @returns LoadManifestResult with manifest and verification status
 * @throws Error if fetch fails, manifest is invalid, or required signature verification fails
 */
export async function loadManifestWithVerification(manifestUrl, registryConfig) {
    let response;
    try {
        response = await fetch(manifestUrl);
    }
    catch (err) {
        throw new Error(`Failed to fetch manifest from "${manifestUrl}": ${err instanceof Error ? err.message : String(err)}`);
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch manifest from "${manifestUrl}": HTTP ${response.status} ${response.statusText}`);
    }
    // Get raw text for signature verification
    let rawJson;
    try {
        rawJson = await response.text();
    }
    catch {
        throw new Error(`Failed to read manifest from "${manifestUrl}"`);
    }
    // Parse JSON
    let data;
    try {
        data = JSON.parse(rawJson);
    }
    catch {
        throw new Error(`Failed to parse manifest from "${manifestUrl}": Invalid JSON`);
    }
    // Validate and parse manifest
    const manifest = parseManifest(data);
    // Verify signature if registry config provided
    let verification;
    if (registryConfig) {
        verification = await verifyManifest(manifestUrl, rawJson, registryConfig);
        // Throw if signature is required and verification failed
        if (registryConfig.requireSignature && !verification.verified && !verification.skipped) {
            throw new Error(`Manifest signature verification failed: ${verification.error || 'Unknown error'}`);
        }
    }
    return { manifest, rawJson, verification };
}
//# sourceMappingURL=manifest.js.map